import { ibanFormatter } from '@asn/formatters';
import { CurrencySymbol, formatCurrency } from '@asn/formatters';

import { Component, OnDestroy, effect, inject, input, signal, computed } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { startWith, Subject, switchMap } from 'rxjs';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';

import { ApContentService, ApContentPipe } from '@ap/content';
import { ErrorMessages } from '@ap/form/errors';
import { ApModalService, ModalClosedReason, ModalClosedResult, ModalOptions } from '@ap/modal';
import {
    ApSelectorModalComponent,
    ApSelectorModalOptions,
    ApSelectorOption,
    ApSelectorSelectionType,
    ApSelectorSubOptionLabels,
    ApSelectorSelectComponent
} from '@ap/selector';
import { ApIbanValidator } from '@ap/input/validators';

import content from './account-selector.content.json';
import { Account, AccountTypeIds } from './account-selector.model';

@Component({
    selector: 'ap-account-selector',
    templateUrl: './account-selector.component.html',
    imports: [ApSelectorSelectComponent, ReactiveFormsModule, ApContentPipe]
})
export class ApAccountSelectorComponent implements OnDestroy {
    private readonly contentService = inject(ApContentService);
    private readonly modalService = inject(ApModalService);

    /**
     * Required - List of accounts to select from.
     */
    public readonly options = input.required<Account[]>();

    /**
     * Type of selection allowed for user.
     * - `single` - user can select one account
     * - `multiple` - user can select one, multiple or all accounts
     */
    public readonly selectionType = input<ApSelectorSelectionType>('single');

    /**
     * Optional control input. Passed on to selector
     */
    public readonly control = input<FormControl<Account[]>>(new FormControl());

    /**
     * Replacement for valuechanges subscription
     * Allows you to watch the value of the control as a signal
     */
    private readonly controlValue = toSignal(
        toObservable(this.control).pipe(switchMap((ctrl) => ctrl.valueChanges.pipe(startWith(this.control().value))))
    );

    constructor() {
        this.contentService.addContentItems(content);

        effect(() => {
            this.setSelectedOptions(this.selectorOptions());
        });
        effect(() => {
            this.setPreselectedValue(this.controlValue());
        });
    }

    protected readonly selectorOptions = computed<ApSelectorOption[]>(() => {
        const options = this.options();
        return options?.map((option) => this.mapSelectorOption(option)).sort((a, b) => Number(a.groupId) - Number(b.groupId)) || [];
    });
    protected selectedOptions = signal<ApSelectorOption[]>(undefined);
    protected messages: ErrorMessages = {
        required: 'accountSelector.error.required'
    };

    private readonly destroy$ = new Subject<void>();

    /**
     * Watch value changes to update selected option
     */
    public setPreselectedValue(controlValue: Account[]): void {
        if (controlValue) {
            this.setSelectedOptionsFromControlValue(controlValue);
        }
    }

    /**
     * On selectorOptions change, preselect if only one option
     */
    private setSelectedOptions(selectorOptions: ApSelectorOption[]): void {
        // If there is no selectorOptions or more than one option, do not preselect
        if (!selectorOptions || selectorOptions.length !== 1) {
            return;
        }

        // If there is only one option, but it has more than one subOption, do not preselect
        if (selectorOptions[0] && selectorOptions[0]?.subOptions && selectorOptions[0]?.subOptions?.length !== 1) {
            return;
        }

        const optionId = selectorOptions[0].id;
        const controlValue = this.control()?.value;

        // If no control value is set, preselect the first option and set the control value
        if (!controlValue || !controlValue[0]) {
            this.selectedOptions.set([selectorOptions[0]]);
            this.control()?.setValue([selectorOptions[0].data]);
            return;
        }

        const controlOptionId = this.generateOptionId(controlValue[0]);

        // Check that the currently set control value is not the same as we are trying to set.
        // This will cause an infinite loop
        if (controlOptionId !== optionId) {
            this.selectedOptions.set([selectorOptions[0]]);
            this.control()?.setValue([selectorOptions[0].data]);
        }
    }

    /**
     * Clear subscriptions on destroy
     */
    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Open selector modal with correct options
     */
    protected openModal(): void {
        const modalOptions = new ModalOptions<ApSelectorModalOptions>({
            options: this.selectorOptions(),
            selectedOptions: this.selectedOptions(),
            groups: [
                { title: null, id: null },
                { title: this.contentService.get('accountSelector.checkingsAccount'), id: AccountTypeIds.checkings },
                { title: this.contentService.get('accountSelector.savingsAccount'), id: AccountTypeIds.savings },
                { title: this.contentService.get('accountSelector.investmentAccount'), id: AccountTypeIds.investment },
                { title: this.contentService.get('accountSelector.otherAccount'), id: AccountTypeIds.other },
                { title: this.contentService.get('accountSelector.mortgageAccount'), id: AccountTypeIds.mortgage },
                { title: this.contentService.get('accountSelector.loanAccount'), id: AccountTypeIds.loan },
                { title: this.contentService.get('accountSelector.loanpartAccount'), id: AccountTypeIds.loanpart }
            ],
            selectionType: this.selectionType()
        });
        modalOptions.title = this.getTitle(this.selectorOptions());

        const modalContainer = this.modalService.open(ApSelectorModalComponent, modalOptions);
        modalContainer.afterClosed().subscribe((result: ModalClosedResult<ApSelectorOption[]>) => {
            const control = this.control();
            if (control) {
                control.markAsDirty();
            }

            if (result.reason === ModalClosedReason.SELECTED) {
                this.selectedOptions.set(result.data);
                control?.setValue(this.selectedOptions().map((option) => option.data));
            } else {
                this.setSelectedOptionsFromControlValue(control.value);
            }
        });
    }

    /**
     * Map plain Account to selector option
     */
    private mapSelectorOption(option: Account, parentId?: string): ApSelectorOption {
        const optionId = this.generateOptionId(option);
        return {
            title: option.alias ? option.alias : option.name,
            text: this.formatIban(option.iban),
            subtitle: this.getBalance(option.balance),
            extra: option.extra,
            id: optionId,
            groupId: option.type ? AccountTypeIds[option.type] : null,
            // If spaarpotten are available add labels
            ...(option.spaarpotten && {
                subOptions: option.spaarpotten.map((subOption) => this.mapSelectorOption(subOption, optionId)),
                labels: this.getLabels(option)
            }),
            // If optionId is the same as prentId it is the main spaarpot. Then Icon should not be shown
            ...(parentId !== optionId && option.spaarpotIcon && { icon: option.spaarpotIcon }),
            data: { ...option }
        };
    }

    /**
     * Format iban if correct iban
     */
    private formatIban(iban: string): string | null {
        const isValidIban = ApIbanValidator.isIban(iban);
        if (!isValidIban) {
            return iban || null;
        }
        return ibanFormatter.formatDisplay(iban);
    }

    /**
     * Calculate correct balance
     */
    private getBalance(balance: number): string | null {
        if (balance === null || balance === undefined) {
            return null;
        }

        return formatCurrency(balance, CurrencySymbol.EUR, 'symbol', '1.2-2', 'nl');
    }

    /**
     * Generate correct labels for spaarpotten in modal
     */
    private getLabels(option: Account): ApSelectorSubOptionLabels {
        const amountOfSpaarpotten = option.spaarpotten?.length ? option.spaarpotten?.length - 1 : 0;
        const descriptionKey = amountOfSpaarpotten === 1 ? 'accountSelector.spaarpot' : 'accountSelector.spaarpotten';
        return {
            description: this.contentService.get(descriptionKey, {
                amount: amountOfSpaarpotten.toString()
            }),
            modalTitle: this.contentService.get('accountSelector.selectSpaarpot')
        };
    }

    /**
     * Set selected options from passed value
     */
    private setSelectedOptionsFromControlValue(controlValue: Account[]): void {
        const selectorOptions = this.selectorOptions();
        if (!selectorOptions || !controlValue) {
            return;
        }

        const selectedControlValueIds = controlValue.map((account: Account) => this.generateOptionId(account));
        this.selectedOptions.set(selectorOptions.map((option) => this.getSelectedOptions(selectedControlValueIds, option)).flat());
    }

    /**
     * Generate unique id for option
     */
    private generateOptionId(option: Account): string {
        return `${option.name?.replace(/\s/g, '-') || ''}-${option.iban || ''}`.toLowerCase();
    }

    /**
     * Check if option or suboptions are selected.
     * Map them to flat array
     */
    private getSelectedOptions(selectedIds: string[], option: ApSelectorOption): ApSelectorOption[] {
        const selectedOptions = [];
        // If subOptions are available only check those if they are selected
        if (!option.subOptions && selectedIds.indexOf(option.id) > -1) {
            selectedOptions.push(option);
        }
        if (option.subOptions) {
            option.subOptions.forEach((subOption) => {
                if (selectedIds.indexOf(subOption.id) > -1) {
                    selectedOptions.push(subOption);
                }
            });
        }
        return selectedOptions;
    }

    private getTitle(options: ApSelectorOption[]): string {
        const regularGroupIds = [AccountTypeIds.checkings, AccountTypeIds.savings, AccountTypeIds.investment, AccountTypeIds.other];
        const groupIds = options.map((option) => option.groupId as number);

        // Check if options contain regular accounts. If so, render regular label
        if (groupIds.some((groupId: number) => regularGroupIds.indexOf(groupId) > -1)) {
            return this.contentService.get('accountSelector.label');
        }

        // Check if options contain a mortgage account. If so, mortgage regular label
        if (groupIds.some((groupId) => groupId === AccountTypeIds.mortgage)) {
            return this.contentService.get('accountSelector.mortgageLabel');
        }

        // Check if options contain a loan account. If so, render loan label
        if (groupIds.some((groupId) => groupId === AccountTypeIds.loan)) {
            return this.contentService.get('accountSelector.loanLabel');
        }

        // Check if options contain a loanpart account. If so, render loanpart label
        if (groupIds.some((groupId) => groupId === AccountTypeIds.loanpart)) {
            return this.contentService.get('accountSelector.loanpartLabel');
        }

        return this.contentService.get('accountSelector.label');
    }
}
